import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import axios from "axios";
import BASE_URL from "../config";
import {
  CaciStyles, PageHeader, Pager, ArticleCard, SkelGrid, SectionNote,
  useReveal, useScrollProgress, useDebounced,
  excerptOf, truncate, formatDate, langPath, localized, localizedImage,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   SEARCH
   ───────────────────────────────────────────────────────────────────────────
   Same endpoint, same params (search / per_page / service_id), same routes.
   What changed:

   · A result set here mixes articles, pages and services, and every card used
     to look identical — you could not tell what you were about to open. Each
     result now carries a colour-coded type badge.
   · The language prefix is preserved. The old code called
     navigate(`/search?...`) with a hard-coded path on every keystroke, so an
     Arabic visitor at /ar/search was silently thrown to /search and lost
     their language route mid-search.
   · The page number lives in the URL, so a result you paged to can actually
     be shared or reloaded. Previously only `keyword` was stored and any
     refresh dropped you back to page 1.
   · Results stay on screen and dim while a new query runs, instead of being
     replaced by a spinner on every keystroke.
   · Pagination uses real disabled buttons; the old markup put `disabled` on
     <a> tags, which does nothing, so "previous" fired on page 1.
   · The empty-query state now invites the visitor to type and focuses the
     field, rather than showing an icon and leaving them to find the input.
   ═══════════════════════════════════════════════════════════════════════════ */

const PER_PAGE_OPTIONS = [6, 12, 24];

/* The API returns a `type` that doubles as the route segment. Anything not in
   this table still links correctly — it simply gets the neutral badge. */
const KIND = {
  article: { icon: "bi-newspaper",         labelKey: "search.kindArticle", fallback: "Actualité" },
  page:    { icon: "bi-file-earmark-text", labelKey: "search.kindPage",    fallback: "Page" },
  service: { icon: "bi-briefcase",         labelKey: "search.kindService", fallback: "Service" },
};

function Search() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [params, setParams] = useSearchParams();

  const keyword = params.get("keyword") || "";
  const serviceId = params.get("service_id") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const perPage = PER_PAGE_OPTIONS.includes(Number(params.get("per")))
    ? Number(params.get("per"))
    : 12;

  const [term, setTerm] = useState(keyword);
  const debounced = useDebounced(term, 400);

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [state, setState] = useState(keyword ? "loading" : "idle"); // idle | loading | ready | error
  const [refetching, setRefetching] = useState(false);

  const cancelRef = useRef(null);
  const alive = useRef(true);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const firstRun = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  /* This page exists to be typed into. If nothing was searched yet, put the
     caret where the visitor is going to go anyway — but never steal focus on
     a phone, where it would throw the keyboard up over the results. */
  useEffect(() => {
    if (keyword) return;
    const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    if (coarse) return;
    inputRef.current && inputRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Push the settled term into the query string.
     setSearchParams (not navigate) is what keeps the language prefix intact:
     it rewrites only the query, leaving /ar/search or /en/search alone. */
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (debounced === keyword) return;
    const next = new URLSearchParams(params);
    if (debounced) next.set("keyword", debounced); else next.delete("keyword");
    next.delete("page");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const patch = useCallback((changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    if (resetPage) next.delete("page");
    setParams(next);
  }, [params, setParams]);

  const fetchResults = useCallback(() => {
    if (cancelRef.current) cancelRef.current.cancel("superseded");

    if (!keyword) {
      setResults([]); setTotal(0); setTotalPages(1);
      setState("idle"); setRefetching(false);
      return Promise.resolve();
    }

    cancelRef.current = axios.CancelToken.source();
    setState((s) => (s === "ready" ? s : "loading"));
    setRefetching(true);

    return axios
      .get(`${BASE_URL}/api/search`, {
        params: {
          page, per_page: perPage, search: keyword,
          service_id: serviceId || undefined,
        },
        cancelToken: cancelRef.current.token,
      })
      .then((res) => {
        if (!alive.current) return;
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        const pg = res.data?.pagination || {};
        setResults(data);
        setTotal(Number(pg.total ?? data.length) || 0);
        setTotalPages(Math.max(1, Number(pg.last_page) || 1));
        setState("ready");
      })
      .catch((err) => {
        if (axios.isCancel(err) || !alive.current) return;
        console.error("Search: request failed —", err);
        setState("error");
      })
      .finally(() => {
        if (alive.current) setRefetching(false);
      });
  }, [keyword, page, perPage, serviceId]);

  useEffect(() => {
    fetchResults();
    return () => cancelRef.current && cancelRef.current.cancel("unmounted");
  }, [fetchResults]);

  const pageRef = useReveal([results, state]);
  const progress = useScrollProgress();

  const goToPage = (p) => {
    patch({ page: p === 1 ? null : p }, { resetPage: false });
    const el = listRef.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };

  const clearSearch = () => {
    setTerm("");
    inputRef.current && inputRef.current.focus();
  };

  const badgeFor = (item) => {
    const meta = KIND[item.type];
    if (!meta) return null;
    return { kind: item.type, icon: meta.icon, label: t(meta.labelKey, meta.fallback) };
  };

  /* The route is /{type}/{id}/{slug}. A missing slug used to produce
     "/article/12/undefined"; the optional segment is simply left off. */
  const linkFor = (item) => {
    const type = item.type || "article";
    return item.slug ? `/${type}/${item.id}/${item.slug}` : `/${type}/${item.id}`;
  };

  const searchTitle = t("search.title");
  const heading = keyword ? `${t("search.resultsFor")} « ${truncate(keyword, 40)} »` : searchTitle;
  const nf = lang === "ar" ? "ar-DZ" : lang === "en" ? "en-US" : "fr-FR";

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <style>{PAGE_CSS}</style>

      <Helmet>
        <title>{keyword || searchTitle} — {t("menu.caci")}</title>
        <meta name="description" content={keyword || searchTitle} />
        <meta property="og:title" content={keyword || searchTitle} />
        <meta property="og:description" content={keyword || searchTitle} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />
        {/* A search result page has nothing durable to offer a crawler, and
            indexing it creates thin duplicates of the pages it links to. */}
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={searchTitle}
        crumbs={[{ label: t("menu.home"), to: langPath(lang, "/") }, { label: searchTitle }]}
        count={
          state === "ready" && keyword
            ? { value: total.toLocaleString(nf), label: t("search.results", "résultats") }
            : null
        }
      />

      {/* ══════════════════ SEARCH FIELD ══════════════════ */}
      <div className="bar">
        <div className="caci-shell">
          <div className="bar-inner">
            <div className="field field-lg">
              <i className="bi bi-search" aria-hidden="true" />
              <input
                ref={inputRef}
                type="search"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={t("search.placeholder")}
                aria-label={t("search.placeholder")}
                autoComplete="off"
                dir="auto"
              />
              {refetching && <span className="field-busy" aria-hidden="true" />}
              {term && (
                <button
                  type="button"
                  className="field-clear"
                  onClick={clearSearch}
                  aria-label={t("home.Close")}
                >
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>

            {keyword && (
              <div className="bar-filters">
                <div className="select">
                  <select
                    value={perPage}
                    onChange={(e) => patch({ per: e.target.value })}
                    aria-label={t("search.perPage")}
                  >
                    {PER_PAGE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n} {t("search.perPage")}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══════════════════ RESULTS ══════════════════ */}
      <section className="caci-section" ref={listRef}>
        <div className="caci-shell">

          {state === "idle" && (
            <SectionNote
              title={t("search.noKeyword")}
              text={t("search.enterKeyword")}
              action={
                <button
                  type="button"
                  className="btn-quiet"
                  onClick={() => inputRef.current && inputRef.current.focus()}
                >
                  <i className="bi bi-search" />
                  {t("search.title")}
                </button>
              }
            />
          )}

          {state === "loading" && <SkelGrid count={perPage > 12 ? 12 : perPage} />}

          {state === "error" && (
            <SectionNote
              state="error"
              title={t("search.errorTitle", "Recherche impossible")}
              text={t("home.section_error", "Cette recherche n'a pas pu aboutir.")}
              action={
                <button type="button" className="btn-quiet" onClick={fetchResults}>
                  <i className="bi bi-arrow-clockwise" />
                  {t("home.retry", "Réessayer")}
                </button>
              }
            />
          )}

          {state === "ready" && results.length === 0 && (
            <SectionNote
              title={t("search.noResults")}
              text={t("search.tryDifferentTerms")}
              action={
                <button type="button" className="btn-quiet" onClick={clearSearch}>
                  <i className="bi bi-arrow-counterclockwise" />
                  {t("search.clear", "Nouvelle recherche")}
                </button>
              }
            />
          )}

          {state === "ready" && results.length > 0 && (
            <>
              <h2 className="res-heading" data-rv="rise" dir="auto">{heading}</h2>

              <div className={`caci-grid${refetching ? " is-busy" : ""}`}>
                {results.map((item, i) => (
                  <ArticleCard
                    key={`${item.type || "x"}-${item.id ?? i}`}
                    index={i}
                    to={linkFor(item)}
                    image={localizedImage(item, "image", lang, BASE_URL)}
                    title={truncate(localized(item, "name", lang), 78)}
                    excerpt={excerptOf(localized(item, "description", lang), 20)}
                    date={
                      item.type === "article"
                        ? formatDate(item.created_at, lang, t("home.UnknownDate"))
                        : null
                    }
                    badge={badgeFor(item)}
                    readLabel={t("home.Read_More")}
                    isRtl={isRtl}
                  />
                ))}
              </div>

              <div className="pager-wrap">
                <Pager
                  page={page}
                  totalPages={totalPages}
                  onChange={goToPage}
                  labels={{
                    first: t("search.first", "Première page"),
                    prev: t("search.prev", "Page précédente"),
                    next: t("search.next", "Page suivante"),
                    last: t("search.last", "Dernière page"),
                    page: t("search.page", "Page"),
                    of: t("search.of", "sur"),
                  }}
                />
              </div>
            </>
          )}

          <p className="caci-sr" aria-live="polite">
            {state === "ready" && keyword ? `${total} ${t("search.results", "résultats")}` : ""}
          </p>
        </div>
      </section>
    </div>
  );
}

const PAGE_CSS = `
/* The search field is the whole point of this page, so it is given more
   presence here than the same control gets on a listing page. */
.caci-page .field-lg { min-height: 60px; }
.caci-page .field-lg input { height: 58px; font-size: 16px; }
.caci-page .field-lg > i { padding-inline-start: 22px; font-size: 17px; }

.caci-page .res-heading {
  margin: 0 0 clamp(22px, 3.4vw, 34px);
  font-family: var(--display);
  font-size: clamp(1.15rem, 3.2vw, 1.6rem);
  font-weight: 700; letter-spacing: -.024em; line-height: 1.24;
  color: var(--ink); text-wrap: balance;
}
.caci-page .caci-grid.is-busy {
  opacity: .45; pointer-events: none; transition: opacity .2s ease;
}
.caci-page .pager-wrap { margin-block-start: clamp(36px, 6vw, 64px); }
`;

export default Search;
